/* global axios */
import ApiClient from './ApiClient';

export const buildContactParams = (page, sortAttr, label, search) => {
  let params = `include_contact_inboxes=false&page=${page}&sort=${sortAttr}`;
  if (search) {
    params = `${params}&q=${encodeURIComponent(search)}`;
  }
  if (label) {
    params = `${params}&labels[]=${encodeURIComponent(label)}`;
  }
  return params;
};

class ContactAPI extends ApiClient {
  constructor() {
    super('contacts', { accountScoped: true });
  }

  get(page, sortAttr = 'name', label = '') {
    let requestURL = `${this.url}?${buildContactParams(
      page,
      sortAttr,
      label,
      ''
    )}`;
    return axios.get(requestURL);
  }

  show(id) {
    return axios.get(`${this.url}/${id}?include_contact_inboxes=false`);
  }

  update(id, data) {
    return axios.patch(`${this.url}/${id}?include_contact_inboxes=false`, data);
  }

  getConversations(contactId, { inboxId } = {}) {
    const params = inboxId ? { inbox_id: inboxId } : {};
    return axios.get(`${this.url}/${contactId}/conversations`, { params });
  }

  getAttachments(contactId, page = 1) {
    return axios.get(`${this.url}/${contactId}/attachments`, {
      params: { page },
    });
  }

  getContactableInboxes(contactId) {
    return axios.get(`${this.url}/${contactId}/contactable_inboxes`);
  }

  getIdentities(contactId) {
    return axios.get(`${this.url}/${contactId}/identities`);
  }

  makeIdentityPrimary(contactId, identityId) {
    return axios.post(
      `${this.url}/${contactId}/identities/${identityId}/make_primary`
    );
  }

  getContactLabels(contactId) {
    return axios.get(`${this.url}/${contactId}/labels`);
  }

  initiateCall(contactId, inboxId, conversationId = null) {
    return axios.post(`${this.url}/${contactId}/call`, {
      inbox_id: inboxId,
      conversation_id: conversationId,
    });
  }

  updateContactLabels(contactId, labels) {
    return axios.post(`${this.url}/${contactId}/labels`, { labels });
  }

  search(search = '', page = 1, sortAttr = 'name', label = '', options = {}) {
    let requestURL = `${this.url}/search?${buildContactParams(
      page,
      sortAttr,
      label,
      search
    )}`;
    return axios.get(requestURL, { signal: options.signal });
  }

  active(page = 1, sortAttr = 'name') {
    let requestURL = `${this.url}/active?${buildContactParams(page, sortAttr)}`;
    return axios.get(requestURL);
  }

  // eslint-disable-next-line default-param-last
  filter(page = 1, sortAttr = 'name', queryPayload) {
    let requestURL = `${this.url}/filter?${buildContactParams(page, sortAttr)}`;
    return axios.post(requestURL, queryPayload);
  }

  importContacts(file) {
    const formData = new FormData();
    formData.append('import_file', file);
    return axios.post(`${this.url}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  destroyCustomAttributes(contactId, customAttributes) {
    return axios.post(`${this.url}/${contactId}/destroy_custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  destroyAvatar(contactId) {
    return axios.delete(`${this.url}/${contactId}/avatar`);
  }

  exportContacts(queryPayload) {
    return axios.post(`${this.url}/export`, queryPayload);
  }

  // Lead Scoring: retorna histórico dos scores IA de um contato (usado na aba
  // "Score de Lead" do ContactInfoPanel).
  getLeadScores(contactId, { limit = 10 } = {}) {
    return axios.get(`${this.url}/${contactId}/lead_scores?limit=${limit}`);
  }

  // Int-F4: grupo de duplicata por waha_lid deste contato (vazio se não há
  // duplicata). Sem contactId, devolve todos os grupos da conta (IF4-C).
  getWahaDuplicates(contactId) {
    const query = contactId ? `?contact_id=${contactId}` : '';
    return axios.get(`${this.url}/waha_duplicates${query}`);
  }

  // SiteUp: exportações já geradas, para baixar pela tela.
  getExports() {
    return axios.get(`${this.url}/exports`);
  }

  // SiteUp: só as contagens (aviso na lista de contatos), sem os grupos.
  getWahaDuplicatesSummary() {
    return axios.get(`${this.url}/waha_duplicates?summary=true`);
  }

  // SiteUp: decisão da equipe sobre um grupo de duplicatas.
  // status: 'snoozed' | 'ignored' | 'pending' (volta para a fila).
  reviewWahaDuplicate(wahaLid, status) {
    return axios.post(`${this.url}/waha_duplicate_review`, {
      waha_lid: wahaLid,
      status,
    });
  }

  // Int-F4: botão "Verificar agora" — reenfileira o enriquecimento WAHA.
  wahaEnrich(contactId) {
    return axios.post(`${this.url}/${contactId}/waha_enrich`);
  }
}

export default new ContactAPI();
